import Image from 'next/image';
import Link from 'next/link';

const logoIconSrc = '/logos/skill4skill-logo-icon.svg';

function FooterLogo() {
  return (
    <Link
      href='/'
      className='flex items-center gap-2.5 text-xl text-brand-forest'
      aria-label='Skill4Skill home'
    >
      <Image
        src={logoIconSrc}
        alt=''
        aria-hidden='true'
        width={36}
        height={36}
        className='size-9 object-contain'
      />
      <span className='font-sans font-bold text-brand-forest'>Skill4Skill</span>
    </Link>
  );
}

const COLUMNS = [
  {
    title: 'Product',
    links: ['Browse Skills', 'Community', 'Trust & Safety', 'FAQs'],
  },
  {
    title: 'Company',
    links: ['Contact', 'Our Story', 'Community guidelines', 'How it works'],
  },
  { title: 'Legal', links: ['Terms', 'Privacy'] },
] as const;

function FooterLink({ label }: { label: string }) {
  const cls = 'hover:text-brand-forest transition-colors';
  switch (label) {
    case 'FAQs':
      return (
        <Link href='/faq' className={cls}>
          {label}
        </Link>
      );
    case 'Contact':
      return (
        <Link href='/contact' className={cls}>
          {label}
        </Link>
      );
    case 'Browse Skills':
      return (
        <Link href='/browse-skills' className={cls}>
          {label}
        </Link>
      );
    case 'Community':
      return (
        <Link href='/community' className={cls}>
          {label}
        </Link>
      );
    case 'Trust & Safety':
      return (
        <Link href='/trust' className={cls}>
          {label}
        </Link>
      );
    case 'How it works':
      return (
        <Link href='/how-it-works' className={cls}>
          {label}
        </Link>
      );
    case 'Community guidelines':
      return (
        <Link href='/community-guidelines' className={cls}>
          {label}
        </Link>
      );
    case 'Our Story':
      return (
        <Link href='/why-choose-us' className={cls}>
          {label}
        </Link>
      );
    case 'Terms':
      return (
        <Link href='/terms' className={cls}>
          {label}
        </Link>
      );
    case 'Privacy':
      return (
        <Link href='/privacy' className={cls}>
          {label}
        </Link>
      );
    default:
      return (
        <a href='#' className={cls}>
          {label}
        </a>
      );
  }
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='currentColor'
      aria-hidden='true'
    >
      <path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='currentColor'
      aria-hidden='true'
    >
      <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='currentColor'
      aria-hidden='true'
    >
      <path d='M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.834 0-3.186.016-3.586.061-4.834.061-1.17.255-1.814.42-2.234.21-.566.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z' />
    </svg>
  );
}

const SOCIAL_CARDS = [
  {
    name: 'LinkedIn',
    handle: 'Skill4Skill',
    href: 'https://www.linkedin.com/company/skill4skill/',
    icon: LinkedInIcon,
    color: '#0A66C2',
    gradient: 'from-[#0A66C2] to-[#0077B5]',
    bg: 'bg-[#0A66C2]/10',
    hoverBg: 'hover:bg-[#0A66C2]/15',
    iconColor: 'text-[#0A66C2]',
    ring: 'hover:ring-[#0A66C2]/40',
  },
  {
    name: 'Facebook',
    handle: 'Skill4Skill',
    href: 'https://www.facebook.com/share/19M1DCfuGR/',
    icon: FacebookIcon,
    color: '#1877F2',
    gradient: 'from-[#1877F2] to-[#42B72A]',
    bg: 'bg-[#1877F2]/10',
    hoverBg: 'hover:bg-[#1877F2]/15',
    iconColor: 'text-[#1877F2]',
    ring: 'hover:ring-[#1877F2]/40',
  },
  {
    name: 'Instagram',
    handle: '@skill4skill_',
    href: 'https://www.instagram.com/skill4skill_',
    icon: InstagramIcon,
    color: '#E4405F',
    gradient: 'from-[#E4405F] via-[#F77737] to-[#FCAF45]',
    bg: 'bg-[#E4405F]/10',
    hoverBg: 'hover:bg-[#E4405F]/15',
    iconColor: 'text-[#E4405F]',
    ring: 'hover:ring-[#E4405F]/40',
  },
] as const;

export function Footer() {
  return (
    <footer className='border-t border-border bg-brand-cream-2'>
      <div className='mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8'>
        <div className='grid gap-10 lg:grid-cols-4'>
          <div>
            <FooterLogo />
            <p className='mt-4 max-w-xs text-sm text-foreground/70'>
              Canada&apos;s first digital skill-sharing platform. Trade what you
              know for what you need.
            </p>
          </div>
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className='font-poppins text-base font-bold text-brand-forest'>
                {col.title}
              </h3>
              <ul className='mt-4 space-y-3 text-sm text-foreground/70'>
                {col.links.map((l) => (
                  <li key={l}>
                    <FooterLink label={l} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className='mt-12 border-t border-border pt-8'>
          <p className='mb-4 text-xs font-semibold uppercase tracking-wider text-foreground/50'>
            Follow us
          </p>
          <div className='flex items-center gap-3'>
            {SOCIAL_CARDS.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.name}
                  href={s.href}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='flex size-10 items-center justify-center rounded-full bg-white text-foreground shadow-sm ring-1 ring-border transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md'
                  aria-label={`Follow Skill4Skill on ${s.name}`}
                >
                  <Icon className='size-4' />
                </a>
              );
            })}
          </div>
        </div>
        <div className='mt-12 flex flex-col items-center justify-between gap-4 border-t border-border pt-6 text-xs text-foreground/60 sm:flex-row'>
          <p>© {new Date().getFullYear()} Skill4Skill. Built in Canada.</p>
          <p>Made with care — trade kindly.</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
